<script setup lang="ts" generic="T = string">
import { computed, ref } from "vue";

import Input from "~/components/dumb/Input.vue";
import Modal from "~/components/dumb/Modal.vue";
import { ChevronLeftIcon, SearchIcon } from "~/icons";
import { ModalPosition, ModalWidth } from "~/shared/enums";

defineProps<{
  entity: T;
  hidden?: boolean;
  includeBack?: boolean;
  title?: string;
  includeDone?: boolean;
  includeSearch?: boolean;
  shouldUpdateKeyboardHeight?: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const searchInput = ref<InstanceType<typeof Input> | null>(null);

const clearSearch = () => {
  if (!searchInput.value) {
    return;
  }
  searchInput.value.value = "";
};

defineExpose({
  clearSearch,
  search: computed(() => searchInput.value?.value),
});
</script>

<template>
  <Modal
    :entity="entity"
    title="Mobile bottom sheet"
    hide-title
    :width="ModalWidth.FULL"
    :position="ModalPosition.BOTTOM"
    :hidden="hidden"
    :overflow-hidden="includeDone || includeSearch"
    custom-styles="!p-0"
    :should-update-keyboard-height="shouldUpdateKeyboardHeight"
    @close="emit('close')">
    <div v-if="includeBack && !(title || includeDone || includeSearch)">
      <div class="h-2" />
      <div class="absolute top-2 left-2 flex">
        <div
          class="flex cursor-pointer items-center rounded-sm p-0.5 text-lt"
          @click="emit('close')"
          @keydown.enter="emit('close')">
          <ChevronLeftIcon class="icon-lg" />
        </div>
      </div>
    </div>
    <div
      v-if="title || includeDone || includeSearch"
      class="-mt-1 flex flex-col gap-2 border-b border-lt pr-4 pb-3 text-base"
      :class="includeBack ? 'pl-2' : 'pl-4'">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div
            v-if="includeBack"
            class="flex cursor-pointer items-center rounded-sm p-0.5 text-lt"
            @click="emit('close')"
            @keydown.enter="emit('close')">
            <ChevronLeftIcon class="icon-lg" />
          </div>
          <span v-if="title" class="font-medium text-hvy">{{ title }}</span>
        </div>
        <button v-if="includeDone" type="button" class="text-primary-base" @click="emit('close')">Done</button>
      </div>
      <Input
        v-if="includeSearch"
        ref="searchInput"
        placeholder="Search"
        label="Search"
        hide-label
        :icon="SearchIcon"
        borderless
        input-classes="w-full px-2 text-base py-1.5 bg-lt rounded-sm"
        class="w-full" />
    </div>
    <slot :entity="entity" />
  </Modal>
</template>
